import type { CaseAttributeKind } from '@tms/shared';
import { SelectMenu, SelectMenuItem } from '../../components/ui';
import { useT } from '../../i18n';
import { useCaseAttributes } from './api';
import { CASE_ATTRIBUTE_MESSAGES } from './messages';

/** Sentinel for "not set": a select value is always a string. */
const NONE = 'none';

export interface CaseAttributeSelectProps {
  projectCode: string;
  kind: CaseAttributeKind;
  value: string | null;
  onChange: (value: string | null) => void;
  id?: string;
  invalid?: boolean;
  describedBy?: string;
  /** Lets a closed dialog mount the field without fetching the list. */
  enabled?: boolean;
}

/**
 * Picks one of the project's layers or modules, or none. The list is the
 * project's own: entries are added in the settings, never typed here, so the
 * same module is not written three ways.
 */
export const CaseAttributeSelect = ({
  projectCode,
  kind,
  value,
  onChange,
  id,
  invalid,
  describedBy,
  enabled = true,
}: CaseAttributeSelectProps) => {
  const { t } = useT();
  const { data: attributes } = useCaseAttributes(projectCode, kind, { enabled });

  return (
    <SelectMenu
      // See EnvironmentSelect: a value that arrives after mount needs a
      // remount for the dropdown to show its name.
      key={value ?? NONE}
      id={id}
      invalid={invalid}
      aria-describedby={describedBy}
      value={value ?? NONE}
      onValueChange={(next) => onChange(next === NONE ? null : next)}
    >
      <SelectMenuItem value={NONE}>{t(CASE_ATTRIBUTE_MESSAGES[kind].none)}</SelectMenuItem>
      {(attributes ?? []).map((attribute) => (
        <SelectMenuItem key={attribute.id} value={attribute.id}>
          {attribute.name}
        </SelectMenuItem>
      ))}
    </SelectMenu>
  );
};
